import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

interface NavLink {
  label: string;
  target: string;
  highlight?: boolean;
}

interface NavbarProps {
  links: NavLink[];
  ctaLabel: string;
  ctaTarget: string;
}

const managementItems = [
  { label: 'Тренинг по лидерству', to: '/menedzhment-i-liderstvo/trening-po-liderstvu' },
  { label: 'Делегирование и постановка задач', to: '/menedzhment-i-liderstvo/delegirovanie-i-postanovka-zadach' },
  { label: 'Обратная связь и сложные разговоры', to: '/menedzhment-i-liderstvo/obratnaya-svyaz-i-slozhnye-razgovory' },
  { label: 'Мотивация и управление командой', to: '/menedzhment-i-liderstvo/motivatsiya-i-upravlenie-komandoy' },
  { label: 'Управление конфликтами', to: '/menedzhment-i-liderstvo/trening-po-upravleniyu-konfliktami' },
  { label: 'Управленческие переговоры', to: '/menedzhment-i-liderstvo/upravlencheskie-peregovory' },
  { label: 'Руководитель в переговорах', to: '/menedzhment-i-liderstvo/rukovoditel-v-peregovorah' },
  { label: 'Управление изменениями', to: '/menedzhment-i-liderstvo/upravlenie-izmeneniyami' },
  { label: 'Развитие управленческих компетенций', to: '/menedzhment-i-liderstvo/razvitie-upravlencheskih-kompetenciy' },
  { label: 'Наставничество и развитие сотрудников', to: '/menedzhment-i-liderstvo/nastavnichestvo-i-razvitie-sotrudnikov' },
];

const encyclopediaItems = [
  { label: 'Что такое корпоративное обучение', to: '/enciklopediya-korporativnogo-obucheniya/chto-takoe-korporativnoe-obuchenie' },
  { label: 'Обучение персонала', to: '/enciklopediya-korporativnogo-obucheniya/obuchenie-personala' },
  { label: 'Обучение отдела продаж', to: '/enciklopediya-korporativnogo-obucheniya/obuchenie-otdela-prodazh' },
  { label: 'Тренинг для руководителей', to: '/enciklopediya-korporativnogo-obucheniya/trening-dlya-rukovoditeley' },
  { label: 'Развитие Soft Skills', to: '/enciklopediya-korporativnogo-obucheniya/razvitie-soft-skills' },
  { label: 'Командообразование', to: '/enciklopediya-korporativnogo-obucheniya/komandoobrazovanie' },
  { label: 'Адаптация сотрудников', to: '/enciklopediya-korporativnogo-obucheniya/adaptaciya-sotrudnikov' },
  { label: 'Корпоративная академия', to: '/enciklopediya-korporativnogo-obucheniya/korporativnaya-akademiya' },
  { label: 'Оценка эффективности обучения', to: '/enciklopediya-korporativnogo-obucheniya/ocenka-effektivnosti-obucheniya' },
  { label: 'Разработка программ обучения', to: '/enciklopediya-korporativnogo-obucheniya/razrabotka-programm-obucheniya' },
];

const salesItems = [
  { label: 'Тренинг по продажам', to: '/prodazhi-i-peregovory/trening-po-prodazham' },
  { label: 'Тренинг по переговорам', to: '/prodazhi-i-peregovory/trening-po-peregovoram' },
  { label: 'Холодные звонки', to: '/prodazhi-i-peregovory/trening-po-holodnym-zvonkam' },
  { label: 'Телефонные продажи', to: '/prodazhi-i-peregovory/trening-po-telefonnym-prodazham' },
  { label: 'Выявление потребностей', to: '/prodazhi-i-peregovory/trening-po-vyyavleniyu-potrebnostey' },
  { label: 'B2B продажи', to: '/prodazhi-i-peregovory/trening-po-prodazham-b2b' },
  { label: 'Работа с клиентами', to: '/prodazhi-i-peregovory/trening-po-rabote-s-klientami' },
  { label: 'Продажи через переписку', to: '/prodazhi-i-peregovory/trening-po-prodazham-cherez-perepisku' },
  { label: 'Работа с возражениями', to: '/prodazhi-i-peregovory/trening-po-rabote-s-vozrazheniyami' },
];

const personnelItems = [
  { label: 'Оценка и развитие сотрудников', to: '/razvitie-personala/otsenka-i-razvitie-sotrudnikov' },
  { label: 'Индивидуальный план развития', to: '/razvitie-personala/individualnyy-plan-razvitiya-sotrudnikov' },
  { label: 'Кадровый резерв', to: '/razvitie-personala/kadrovyy-rezerv' },
];

export default function Navbar({ links, ctaLabel, ctaTarget }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [encyclopediaOpen, setEncyclopediaOpen] = useState(false);
  const [salesOpen, setSalesOpen] = useState(false);
  const [managementOpen, setManagementOpen] = useState(false);
  const [personnelOpen, setPersonnelOpen] = useState(false);
  const [encyclopediaMobileOpen, setEncyclopediaMobileOpen] = useState(false);
  const [salesMobileOpen, setSalesMobileOpen] = useState(false);
  const [managementMobileOpen, setManagementMobileOpen] = useState(false);
  const [personnelMobileOpen, setPersonnelMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const salesDropdownRef = useRef<HTMLDivElement>(null);
  const managementDropdownRef = useRef<HTMLDivElement>(null);
  const personnelDropdownRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const salesCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const managementCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const personnelCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Scroll detection — внутри компонента, единое поведение на всех страницах
  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    handleScroll(); // проверяем сразу после сброса
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setEncyclopediaOpen(false);
      }
      if (salesDropdownRef.current && !salesDropdownRef.current.contains(e.target as Node)) {
        setSalesOpen(false);
      }
      if (managementDropdownRef.current && !managementDropdownRef.current.contains(e.target as Node)) {
        setManagementOpen(false);
      }
      if (personnelDropdownRef.current && !personnelDropdownRef.current.contains(e.target as Node)) {
        setPersonnelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setEncyclopediaOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setEncyclopediaOpen(false);
    }, 200);
  };

  const handleSalesMouseEnter = () => {
    if (salesCloseTimeoutRef.current) {
      clearTimeout(salesCloseTimeoutRef.current);
    }
    setSalesOpen(true);
  };

  const handleSalesMouseLeave = () => {
    salesCloseTimeoutRef.current = setTimeout(() => {
      setSalesOpen(false);
    }, 200);
  };

  const handleManagementMouseEnter = () => {
    if (managementCloseTimeoutRef.current) {
      clearTimeout(managementCloseTimeoutRef.current);
    }
    setManagementOpen(true);
  };

  const handleManagementMouseLeave = () => {
    managementCloseTimeoutRef.current = setTimeout(() => {
      setManagementOpen(false);
    }, 200);
  };

  const handlePersonnelMouseEnter = () => {
    if (personnelCloseTimeoutRef.current) {
      clearTimeout(personnelCloseTimeoutRef.current);
    }
    setPersonnelOpen(true);
  };

  const handlePersonnelMouseLeave = () => {
    personnelCloseTimeoutRef.current = setTimeout(() => {
      setPersonnelOpen(false);
    }, 200);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
      <div className="w-full px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center cursor-pointer">
              <img
                src="https://static.readdy.ai/image/045fa1b133efd9567759baab28602a6d/61a59271a80ff4d07ef88e35462987b3.png"
                alt="Business Training"
                className="h-10 w-auto"
              />
            </Link>
          </div>

          {/* Desktop menu */}
          <div className="hidden lg:flex items-start justify-between flex-1 ml-6">
            {/* Left side: two-row layout */}
            <div className="flex-1">
              {/* Row 1: Site-wide navigation */}
              <div className="flex items-center gap-4 xl:gap-6 flex-wrap pt-0.5">
                {/* Энциклопедия dropdown */}
                <div
                  ref={dropdownRef}
                  className="relative"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                <Link
                  to="/enciklopediya-korporativnogo-obucheniya"
                  className={`flex items-center gap-1 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}
                >
                  Энциклопедия
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${encyclopediaOpen ? 'rotate-180' : ''}`}></i>
                </Link>

                {/* Dropdown panel */}
                <div
                  className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 py-3 w-[360px] transition-all duration-200 origin-top ${encyclopediaOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'}`}
                >
                  <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Энциклопедия корпоративного обучения
                  </div>
                  <div className="mt-1">
                    {encyclopediaItems.map((item, index) => (
                      <Link
                        key={index}
                        to={item.to}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                        onClick={() => setEncyclopediaOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sales dropdown */}
              <div
                ref={salesDropdownRef}
                className="relative"
                onMouseEnter={handleSalesMouseEnter}
                onMouseLeave={handleSalesMouseLeave}
              >
                <Link
                  to="/prodazhi-i-peregovory"
                  className={`flex items-center gap-1 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}
                >
                  Продажи и переговоры
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${salesOpen ? 'rotate-180' : ''}`}></i>
                </Link>

                <div
                  className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 py-3 w-[280px] transition-all duration-200 origin-top ${salesOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'}`}
                >
                  <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Продажи и переговоры
                  </div>
                  <div className="mt-1">
                    {salesItems.map((item, index) => (
                      <Link
                        key={index}
                        to={item.to}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                        onClick={() => setSalesOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Менеджмент и лидерство dropdown */}
              <div
                ref={managementDropdownRef}
                className="relative"
                onMouseEnter={handleManagementMouseEnter}
                onMouseLeave={handleManagementMouseLeave}
              >
                <Link
                  to="/menedzhment-i-liderstvo"
                  className={`flex items-center gap-1 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}
                >
                  Менеджмент и лидерство
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${managementOpen ? 'rotate-180' : ''}`}></i>
                </Link>

                <div
                  className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 py-3 w-[320px] transition-all duration-200 origin-top ${managementOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'}`}
                >
                  <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Менеджмент и лидерство
                  </div>
                  <div className="mt-1">
                    {managementItems.map((item, index) => (
                      <Link
                        key={index}
                        to={item.to}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors cursor-pointer whitespace-normal"
                        onClick={() => setManagementOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Развитие персонала dropdown */}
              <div
                ref={personnelDropdownRef}
                className="relative"
                onMouseEnter={handlePersonnelMouseEnter}
                onMouseLeave={handlePersonnelMouseLeave}
              >
                <Link
                  to="/razvitie-personala"
                  className={`flex items-center gap-1 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}
                >
                  Развитие персонала
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${personnelOpen ? 'rotate-180' : ''}`}></i>
                </Link>

                <div
                  className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 py-3 w-[300px] transition-all duration-200 origin-top ${personnelOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'}`}
                >
                  <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Развитие персонала
                  </div>
                  <div className="mt-1">
                    {personnelItems.map((item, index) => (
                      <Link
                        key={index}
                        to={item.to}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors cursor-pointer whitespace-normal"
                        onClick={() => setPersonnelOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* О тренере link */}
              <Link
                to="/o-trenere"
                className={`text-sm font-medium transition-colors hover:text-teal-600 cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700' : 'text-white'}`}
              >
                О тренере
              </Link>

              {/* Контакты link */}
              <Link
                to="/contacts"
                className={`text-sm font-medium transition-colors hover:text-teal-600 cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700' : 'text-white'}`}
              >
                Контакты
              </Link>
            </div>

            {/* Row 2: Page-specific anchor links */}
            {links.length > 0 && (
              <div className="flex items-center gap-4 xl:gap-6 flex-wrap pt-1">
                {links.map((link) => (
                  <button
                    key={link.target}
                    onClick={() => scrollTo(link.target)}
                    className={
                      link.highlight
                        ? `inline-flex items-center gap-1.5 text-sm font-bold transition-colors whitespace-nowrap cursor-pointer px-3 py-1.5 rounded-full border ${isScrolled ? 'bg-teal-600 text-white border-teal-600 hover:bg-teal-700' : 'bg-teal-500/25 text-white border-teal-300/70 hover:bg-teal-500/40'}`
                        : `text-sm font-medium transition-colors hover:text-teal-600 cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-600' : 'text-white/90'}`
                    }
                  >
                    {link.highlight && <i className="ri-calculator-line"></i>}
                    {link.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* CTA — always right, never wraps */}
          <button
            onClick={() => scrollTo(ctaTarget)}
            className="bg-teal-600 text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer flex-shrink-0 mt-0.5"
          >
            {ctaLabel}
          </button>
        </div>

          {/* Hamburger button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}
            aria-label={mobileOpen ? 'Закрыть меню' : 'Открыть меню'}
          >
            <i className={`text-2xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile overlay */}
      <div
        className={`lg:hidden fixed inset-0 top-[72px] bg-black/50 backdrop-blur-sm transition-opacity duration-300 z-40 ${mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setMobileOpen(false)}
      ></div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden fixed top-[72px] left-0 right-0 bg-white shadow-2xl z-50 rounded-b-2xl transition-all duration-300 ease-out overflow-hidden ${mobileOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="flex flex-col overflow-y-auto max-h-[calc(80vh-72px)]">
          {/* Encyclopedia mobile */}
          <div className="border-b border-gray-100">
            <button
              onClick={() => setEncyclopediaMobileOpen(!encyclopediaMobileOpen)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-teal-700 hover:bg-teal-50 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-book-read-line"></i>
                Энциклопедия
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${encyclopediaMobileOpen ? 'rotate-180' : ''}`}></i>
            </button>
            <div className={`transition-all duration-300 overflow-hidden ${encyclopediaMobileOpen ? 'max-h-[600px]' : 'max-h-0'}`}>
              <div className="bg-gray-50 py-1">
                {encyclopediaItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.to}
                    className="block px-8 py-2.5 text-sm text-gray-700 hover:text-teal-700 hover:bg-white transition-colors cursor-pointer"
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Sales mobile */}
          <div className="border-b border-gray-100">
            <button
              onClick={() => setSalesMobileOpen(!salesMobileOpen)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-teal-700 hover:bg-teal-50 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-line-chart-line"></i>
                Продажи и переговоры
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${salesMobileOpen ? 'rotate-180' : ''}`}></i>
            </button>
            <div className={`transition-all duration-300 overflow-hidden ${salesMobileOpen ? 'max-h-[300px]' : 'max-h-0'}`}>
              <div className="bg-gray-50 py-1">
                {salesItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.to}
                    className="block px-8 py-2.5 text-sm text-gray-700 hover:text-teal-700 hover:bg-white transition-colors cursor-pointer"
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Менеджмент и лидерство mobile */}
          <div className="border-b border-gray-100">
            <button
              onClick={() => setManagementMobileOpen(!managementMobileOpen)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-teal-700 hover:bg-teal-50 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-vip-crown-line"></i>
                Менеджмент и лидерство
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${managementMobileOpen ? 'rotate-180' : ''}`}></i>
            </button>
            <div className={`transition-all duration-300 overflow-hidden ${managementMobileOpen ? 'max-h-[300px]' : 'max-h-0'}`}>
              <div className="bg-gray-50 py-1">
                {managementItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.to}
                    className="block px-8 py-2.5 text-sm text-gray-700 hover:text-teal-700 hover:bg-white transition-colors cursor-pointer"
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Развитие персонала mobile */}
          <div className="border-b border-gray-100">
            <button
              onClick={() => setPersonnelMobileOpen(!personnelMobileOpen)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-teal-700 hover:bg-teal-50 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-seedling-line"></i>
                Развитие персонала
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${personnelMobileOpen ? 'rotate-180' : ''}`}></i>
            </button>
            <div className={`transition-all duration-300 overflow-hidden ${personnelMobileOpen ? 'max-h-[300px]' : 'max-h-0'}`}>
              <div className="bg-gray-50 py-1">
                {personnelItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.to}
                    className="block px-8 py-2.5 text-sm text-gray-700 hover:text-teal-700 hover:bg-white transition-colors cursor-pointer"
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* О тренере */}
          <Link
            to="/o-trenere"
            className="w-full text-left px-4 py-3 text-gray-700 font-medium hover:bg-gray-50 transition-colors cursor-pointer text-sm border-b border-gray-50"
            onClick={() => setMobileOpen(false)}
          >
            <span className="flex items-center gap-2">
              <i className="ri-user-star-line"></i>
              О тренере
            </span>
          </Link>

          {/* Контакты */}
          <Link
            to="/contacts"
            className="w-full text-left px-4 py-3 text-gray-700 font-medium hover:bg-gray-50 transition-colors cursor-pointer text-sm border-b border-gray-50"
            onClick={() => setMobileOpen(false)}
          >
            <span className="flex items-center gap-2">
              <i className="ri-mail-line"></i>
              Контакты
            </span>
          </Link>

          {/* Regular links */}
          {links.map((link) => (
            <button
              key={link.target}
              onClick={() => scrollTo(link.target)}
              className={
                link.highlight
                  ? 'w-full text-left px-4 py-3 font-bold text-teal-700 bg-teal-50 hover:bg-teal-100 transition-colors cursor-pointer text-sm border-b border-gray-50 flex items-center gap-2'
                  : 'w-full text-left px-4 py-3 text-gray-700 font-medium hover:bg-gray-50 transition-colors cursor-pointer text-sm border-b border-gray-50'
              }
            >
              {link.highlight && <i className="ri-calculator-line"></i>}
              {link.label}
            </button>
          ))}

          {/* CTA */}
          <div className="p-4">
            <button
              onClick={() => scrollTo(ctaTarget)}
              className="w-full bg-teal-600 text-white px-4 py-3 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer text-center whitespace-nowrap"
            >
              {ctaLabel}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}